/**
 * Détection des capacités navigateur, sans dépendance externe.
 * Les fonctions acceptent leurs entrées en paramètre pour rester testables.
 */

export interface InAppBrowserInfo {
  inApp: boolean;
  app?: 'instagram' | 'tiktok' | 'facebook' | 'other';
}

/** Navigateurs intégrés aux apps sociales : getUserMedia y est souvent bloqué. */
export function detectInAppBrowser(ua: string = navigator.userAgent): InAppBrowserInfo {
  const s = ua.toLowerCase();
  if (s.includes('instagram')) return { inApp: true, app: 'instagram' };
  if (s.includes('bytedance') || s.includes('tiktok') || s.includes('musical_ly')) {
    return { inApp: true, app: 'tiktok' };
  }
  if (s.includes('fban') || s.includes('fbav') || s.includes('fb_iab')) {
    return { inApp: true, app: 'facebook' };
  }
  // WebView Android générique (" wv)"), Line, Google app…
  if (/\bwv\)/.test(s) || s.includes(' line/') || s.includes('gsa/')) {
    return { inApp: true, app: 'other' };
  }
  return { inApp: false };
}

export function isIOS(ua: string = navigator.userAgent): boolean {
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS se présente comme macOS mais est tactile
  return typeof navigator !== 'undefined'
    && navigator.platform === 'MacIntel'
    && (navigator.maxTouchPoints ?? 0) > 1;
}

export function hasCameraSupport(): boolean {
  return typeof navigator !== 'undefined'
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === 'function';
}

export function hasMediaRecorder(): boolean {
  return typeof window !== 'undefined' && 'MediaRecorder' in window;
}

export function hasCanvasCaptureStream(): boolean {
  return typeof HTMLCanvasElement !== 'undefined'
    && typeof (HTMLCanvasElement.prototype as unknown as { captureStream?: unknown })
      .captureStream === 'function';
}

/** Types MIME testés dans l'ordre. mp4 en premier pour Safari iOS. */
export const RECORDING_MIME_CANDIDATES = [
  'video/mp4;codecs=avc1',
  'video/mp4',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
] as const;

export function pickSupportedMimeType(
  candidates: readonly string[] = RECORDING_MIME_CANDIDATES,
  isSupported: (mime: string) => boolean = (mime) =>
    hasMediaRecorder() && MediaRecorder.isTypeSupported(mime),
): string | null {
  for (const mime of candidates) {
    try {
      if (isSupported(mime)) return mime;
    } catch {
      /* certains navigateurs jettent sur isTypeSupported */
    }
  }
  return null;
}

export function fileExtensionForMime(mime: string): string {
  return mime.startsWith('video/mp4') ? 'mp4' : 'webm';
}

/** Web Share API niveau 2 (partage de fichiers). */
export function canShareFile(file: File): boolean {
  const nav = navigator as Navigator & { canShare?: (data?: ShareData) => boolean };
  return typeof nav.share === 'function'
    && typeof nav.canShare === 'function'
    && nav.canShare({ files: [file] });
}

export function isSecureContextForCamera(): boolean {
  return window.isSecureContext || location.hostname === 'localhost';
}
